// Copyright (c) 2026 Goffy. All Rights Reserved.

import { useEffect } from 'react';

import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { getSharedBffWebSocket } from '@/services/bffWebSocket';

/** Shorter queries are still being typed; they match almost anything and tell the team nothing. */
const MIN_QUERY_LENGTH = 3;
/** Wait for the user to stop typing before deciding the query really found nothing. */
const REPORT_DELAY_MS = 1500;

/** Queries already reported this app session — a user retyping the same course must not spam Slack. */
const reportedQueries = new Set<string>();

type Options = {
  /** The raw search text as typed. */
  query: string;
  /** Whether the local filter found at least one course for `query`. */
  hasResults: boolean;
  /** Only report while the picker is open with a loaded catalogue — never from a stale/initial value. */
  active: boolean;
};

/**
 * Reports a course search that found nothing to the BFF (`course.search.miss`), which forwards it
 * to Slack so the missing course can be added. Debounced, length-gated and deduped per session so
 * partial typing and retries do not flood the channel. Fire-and-forget: no reply is awaited.
 */
export function useCourseSearchMissReport({ query, hasResults, active }: Options): void {
  useEffect(() => {
    if (!active || hasResults) return;
    const normalized = query.trim();
    if (normalized.length < MIN_QUERY_LENGTH) return;
    const key = normalized.toLowerCase();
    if (reportedQueries.has(key)) return;

    const timer = setTimeout(() => {
      if (reportedQueries.has(key)) return;
      reportedQueries.add(key);
      getSharedBffWebSocket(getApiBaseUrl()).sendCourseSearchMiss({
        query: normalized,
        appVersion: typeof process !== 'undefined' ? process.env.EXPO_PUBLIC_APP_VERSION : undefined,
        timestamp: new Date().toISOString(),
      });
    }, REPORT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [active, hasResults, query]);
}

export const __testOnly = {
  resetReportedQueries: () => reportedQueries.clear(),
  REPORT_DELAY_MS,
};
